import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { ChevronDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatMoneyCompact } from "@/lib/format";
import { useSuretyMarket } from "@/hooks/queries/useSuretyMarket";
import { useDepartments, useDistricts, useProvinces } from "@/hooks/queries/useDepartments";
import {
  BarRow,
  Chip,
  EmptyNote,
  MonthSelect,
  PageHeader,
  SectionCard,
} from "@/components/surety/SuretyUI";
import { WinnerProspects } from "@/components/surety/ProspectList";
import { FilterChip } from "@/components/team/BoardFilterRows";
import { CheckboxList } from "@/components/filters/FilterBar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { translateSubject } from "@/components/contractings/pills";
import {
  PROSPECT_TABS,
  prospectTab,
  type ProspectTab,
} from "@/components/surety/prospectTabs";

const SUBJECTS = ["works", "goods", "services", "consulting"];
// Los cortes del filtro "Desde / hasta" del monto adjudicado, en soles.
const AMOUNT_STEPS = [500_000, 1_000_000, 2_000_000, 5_000_000, 10_000_000, 20_000_000, 50_000_000];

// Objeto, monto y lugar se recuerdan en el navegador para no elegirlos en cada visita.
const STORAGE_KEY = "surety:prospect-filters:v1";

type PersistedProspectFilters = {
  subject: string | null;
  minAmount: number | null;
  maxAmount: number | null;
  regions: string[];
  provinces: string[];
  districts: string[];
};

function loadPersistedFilters(): Partial<PersistedProspectFilters> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<PersistedProspectFilters>) : {};
  } catch {
    return {};
  }
}

// Las regiones vienen de fuentes distintas (geografía de la obra o SUNAT): se
// comparan sin mayúsculas ni tildes.
const plain = (text: string | null) =>
  (text ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();

// Así llama el backend (suretymarket.service.ts) a la región de una buena pro sin departamento.
const NO_REGION = "Sin región";

/** Opciones de un filtro de lugar, en orden alfabético, sin repetir y con "Sin región" al final. */
const placeOptions = (names: (string | null)[]) =>
  [...new Set(names.filter((name): name is string => !!name))]
    .sort((a, b) => Number(a === NO_REGION) - Number(b === NO_REGION) || a.localeCompare(b, "es"))
    .map((name) => ({ value: name, label: name }));

/** Si el lugar de una fila está entre los elegidos; sin elegidos, entra todo. */
const inPlaces = (selected: string[], value: string) =>
  selected.length === 0 || selected.some((place) => plain(place) === plain(value));

/** Saca el valor si ya estaba elegido; si no, lo suma. */
const toggle = (selected: string[], value: string) =>
  selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value];

/** Mercado del mes y prospectos. Los gráficos filtran la lista de abajo. */
export default function SuretyMarket() {
  const [month, setMonth] = useState<string | undefined>();
  const { data, isLoading, isError } = useSuretyMarket(month);
  const [tab, setTab] = useState<ProspectTab>("winners");
  const [saved] = useState(loadPersistedFilters);
  const [regions, setRegions] = useState<string[]>(saved.regions ?? []);
  const [provinces, setProvinces] = useState<string[]>(saved.provinces ?? []);
  const [districts, setDistricts] = useState<string[]>(saved.districts ?? []);
  // El catálogo completo, como en las líneas de negocio: un mes con pocos ganadores
  // no debe dejar el filtro sin opciones. Provincia y distrito bajan de a un solo padre.
  const { data: departmentCatalog = [] } = useDepartments();
  const singleRegionId =
    regions.length === 1
      ? departmentCatalog.find((d) => plain(d.department_name) === plain(regions[0]!))?.department_id
      : undefined;
  const { data: provinceCatalog = [] } = useProvinces(singleRegionId);
  const singleProvinceId =
    provinces.length === 1
      ? provinceCatalog.find((p) => plain(p.province_name) === plain(provinces[0]!))?.province_id
      : undefined;
  const { data: districtCatalog = [] } = useDistricts(singleProvinceId);
  const [subject, setSubject] = useState<string | null>(saved.subject ?? null);
  // `null` = sin piso / sin tope.
  const [minAmount, setMinAmount] = useState<number | null>(saved.minAmount ?? null);
  const [maxAmount, setMaxAmount] = useState<number | null>(saved.maxAmount ?? null);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ subject, minAmount, maxAmount, regions, provinces, districts }),
      );
    } catch {
      // Sin almacenamiento (modo privado): los filtros duran solo la visita.
    }
  }, [subject, minAmount, maxAmount, regions, provinces, districts]);

  /** Tocar los departamentos suelta provincias y distritos: dependen de un departamento solo. */
  const chooseRegions = (next: string[]) => {
    setRegions(next);
    setProvinces([]);
    setDistricts([]);
  };

  /** Tocar las provincias suelta los distritos: dependen de una provincia sola. */
  const chooseProvinces = (next: string[]) => {
    setProvinces(next);
    setDistricts([]);
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }
  if (isError || !data) {
    return <div className="mx-auto w-full max-w-3xl px-4 py-24"><EmptyNote>No se pudo cargar el mercado.</EmptyNote></div>;
  }

  const inRegion = (department: string | null) => inPlaces(regions, department ?? NO_REGION);
  // Del mes salen "Sin región" y lo que el catálogo escriba distinto; lo elegido, para que siga marcado.
  const departmentOptions = placeOptions([
    ...departmentCatalog.map((d) => d.department_name),
    ...data.prospects.winners.map((row) => row.department ?? NO_REGION),
    ...regions,
  ]);
  const provinceOptions = placeOptions([
    ...provinceCatalog.map((p) => p.province_name),
    ...data.prospects.winners.filter((row) => inRegion(row.department)).map((row) => row.province),
    ...provinces,
  ]);
  const districtOptions = placeOptions([
    ...districtCatalog.map((d) => d.district_name),
    ...data.prospects.winners
      .filter((row) => inRegion(row.department) && inPlaces(provinces, row.province ?? ""))
      .map((row) => row.district),
    ...districts,
  ]);
  const winners = data.prospects.winners.filter(
    (row) =>
      inRegion(row.department) &&
      inPlaces(provinces, row.province ?? "") &&
      inPlaces(districts, row.district ?? "") &&
      (!subject || row.subject === subject) &&
      (minAmount === null || row.amount >= minAmount) &&
      (maxAmount === null || row.amount <= maxAmount),
  );
  // Por buena pro, la más reciente primero. El consentimiento no sirve para ordenar:
  // el SEACE no publica su fecha, solo sabemos cuándo lo vimos.
  const tabRows = winners
    .filter(prospectTab(tab).matches)
    .sort((a, b) => Date.parse(b.awardedAt) - Date.parse(a.awardedAt));

  const tabs = PROSPECT_TABS.map((item) => ({
    ...item,
    count: winners.filter(item.matches).length,
  }));
  const maxRegion = Math.max(...data.regions.map((r) => r.guarantees), 1);
  const maxIssuer = Math.max(...data.issuers.map((i) => i.count), 1);
  const filters = [...regions, ...provinces, ...districts].join(" · ");
  const periodLabel =
    data.month === dayjs().format("YYYY-MM") ? "en lo que va del mes" : "en el mes";

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      <PageHeader
        eyebrow="Obras públicas · todo el país"
        title="Mercado"
        accent={<MonthSelect value={data.month} onChange={setMonth} className="text-amber-700" />}
        subtitle="Dónde está la demanda de garantías y a quién llamar primero. El mes cambia el mercado y sus ganadores. Toca una región para filtrar la lista."
      />

      <div className="flex flex-col gap-5">
        <section className="grid gap-4 rounded-2xl border bg-card p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Buenas pro de obra", String(data.market.awards), `${data.market.awards ? Math.round((data.market.consortiumAwards / data.market.awards) * 100) : 0}% ganadas por consorcios`],
            ["Adjudicado", formatMoneyCompact(data.market.amount), periodLabel],
            ["Fianzas de fiel cumplimiento", formatMoneyCompact(data.market.guarantees), "10%, antes de retenciones"],
            ["Lo que ganó tu cartera", `${(data.market.portfolioShare * 100).toFixed(1)}%`, `${formatMoneyCompact(data.market.portfolioAmount)} del total`],
          ].map(([label, value, hint]) => (
            <div key={label}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-slate-50">{value}</p>
              <p className="text-xs text-slate-500">{hint}</p>
            </div>
          ))}
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <SectionCard
            title="Dónde se van a necesitar fianzas"
            subtitle={`Fiel cumplimiento implícito por región ${periodLabel} · toca para filtrar`}
          >
            <div className="flex flex-col gap-1">
              {data.regions.map((row) => (
                <BarRow
                  key={row.region}
                  label={row.region}
                  value={formatMoneyCompact(row.guarantees)}
                  pct={(row.guarantees / maxRegion) * 100}
                  barClassName="bg-primary"
                  active={regions.includes(row.region)}
                  dimmed={regions.length > 0 && !regions.includes(row.region)}
                  onClick={() => chooseRegions(toggle(regions, row.region))}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Quién emite hoy"
            subtitle={`${data.guaranteeSample} garantías de contratos leídas`}
          >
            {data.issuers.length === 0 ? (
              <EmptyNote>Todavía no hay garantías leídas.</EmptyNote>
            ) : (
              <div className="flex flex-col gap-1">
                {data.issuers.map((row) => (
                  <BarRow
                    key={row.issuer}
                    label={row.issuer}
                    value={String(row.count)}
                    pct={(row.count / maxIssuer) * 100}
                    barClassName="bg-amber-500"
                  />
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              <Chip tone="info">{data.retentions} retenciones sin fianza</Chip>
              {data.brokers.length > 0 && (
                <Chip tone="info">Corredores: {data.brokers.slice(0, 5).join(", ")}</Chip>
              )}
            </div>
          </SectionCard>
        </div>

        <section className="rounded-2xl border bg-card shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3 px-5 pt-5">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900 dark:text-slate-50">Prospectos</h2>
              <p className="text-xs text-slate-500">
                Ganadores del mes, tus clientes incluidos, de la buena pro más reciente
                a la más vieja
              </p>
            </div>
            {filters && (
              <button
                type="button"
                onClick={() => chooseRegions([])}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white"
              >
                Filtrando: {filters}
                <X className="size-3.5" aria-hidden />
                <span className="sr-only">Quitar filtro</span>
              </button>
            )}
          </div>

          <div role="tablist" aria-label="Fuentes de prospectos" className="mt-3 flex flex-wrap gap-1 border-b px-4">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                onClick={() => setTab(item.id)}
                className={cn(
                  "-mb-px flex cursor-pointer items-center gap-2 border-b-2 px-3 py-3 text-sm font-bold transition-colors",
                  tab === item.id
                    ? "border-amber-500 text-amber-700"
                    : "border-transparent text-slate-500 hover:text-slate-800",
                )}
              >
                {item.label}
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-xs",
                    tab === item.id ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600",
                  )}
                >
                  {item.count}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-2 border-b px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Objeto
              </span>
              {[null, ...SUBJECTS].map((value) => (
                <FilterChip
                  key={value ?? "all"}
                  tone="dark"
                  active={subject === value}
                  onClick={() => setSubject(value)}
                >
                  {value ? translateSubject(value) : "Todos"}
                </FilterChip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Monto
              </span>
              <AmountSelect
                label="Desde"
                emptyLabel="Cualquiera"
                value={minAmount}
                steps={AMOUNT_STEPS}
                onChange={(value) => {
                  setMinAmount(value);
                  // Un tope por debajo del piso nuevo deja la lista vacía: se suelta.
                  if (value !== null && maxAmount !== null && maxAmount <= value) setMaxAmount(null);
                }}
              />
              <AmountSelect
                label="hasta"
                emptyLabel="Sin tope"
                value={maxAmount}
                steps={AMOUNT_STEPS.filter((step) => minAmount === null || step > minAmount)}
                onChange={setMaxAmount}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Región
              </span>
              <PlaceSelect
                label="Departamento"
                emptyLabel="Todos"
                options={departmentOptions}
                selected={regions}
                onChange={chooseRegions}
              />
              {regions.length === 1 && (
                <PlaceSelect
                  label="provincia"
                  emptyLabel="Todas"
                  options={provinceOptions}
                  selected={provinces}
                  onChange={chooseProvinces}
                />
              )}
              {provinces.length === 1 && (
                <PlaceSelect
                  label="distrito"
                  emptyLabel="Todos"
                  options={districtOptions}
                  selected={districts}
                  onChange={setDistricts}
                />
              )}
            </div>
          </div>

          <p className="border-b bg-slate-50 px-5 py-3 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            {prospectTab(tab).hint}
          </p>

          <div>
            {/* La key reinicia el "ver más" al cambiar de pestaña o de filtro. */}
            <WinnerProspects key={`${data.month}-${tab}-${subject}-${minAmount}-${maxAmount}-${filters}`} rows={tabRows} />
          </div>
        </section>
      </div>
    </div>
  );
}

/** Pastilla de filtro: mismos colores que FilterChip tone="dark". */
const pillClass = (active: boolean) =>
  cn(
    "cursor-pointer rounded-full border px-3 py-1.5 text-[13px] outline-none transition-colors",
    active
      ? "border-primary bg-primary font-semibold text-primary-foreground"
      : "border-slate-200 bg-card text-slate-600 hover:border-slate-300 dark:border-slate-800 dark:text-slate-300",
  );

/** Un extremo del filtro de monto: "Desde" o "hasta", con cortes fijos. */
function AmountSelect({
  label,
  emptyLabel,
  value,
  steps,
  onChange,
}: {
  label: string;
  emptyLabel: string;
  value: number | null;
  steps: number[];
  onChange: (value: number | null) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-slate-500">
      {label}
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
        className={pillClass(value !== null)}
      >
        <option value="">{emptyLabel}</option>
        {steps.map((step) => (
          <option key={step} value={step}>
            {formatMoneyCompact(step)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Filtro de lugar con varios elegibles: la pastilla abre la lista con casillas. */
function PlaceSelect({
  label,
  emptyLabel,
  options,
  selected,
  onChange,
}: {
  label: string;
  emptyLabel: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const summary =
    selected.length === 0 ? emptyLabel : selected.length === 1 ? selected[0] : `${selected.length} elegidos`;
  return (
    <div className="flex items-center gap-1.5 text-sm text-slate-500">
      {label}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className={cn(pillClass(selected.length > 0), "flex items-center gap-1")}>
            {summary}
            <ChevronDown className="size-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64">
          <CheckboxList
            title={label}
            options={options}
            selected={selected}
            onToggle={(value) => onChange(toggle(selected, value))}
            onClear={() => onChange([])}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
